// Phase 3 — Business Workspace Selector.
// Renders in the AppShell for Business plan users. Free/Starter/Pro
// users see nothing (they only ever have one primary workspace).
import { useEffect, useState, useCallback } from "react";
import { Building2, ChevronsUpDown, Plus, Check, Archive } from "lucide-react";
import { toast } from "sonner";
import api, { setActiveWorkspaceId, getActiveWorkspaceId } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

export default function WorkspaceSelector() {
  const [state, setState] = useState({ workspaces: [], count: 1, limit: 4,
    can_add: false, is_business_plan: false, is_owner: true,
    multi_business_enabled: false, primary_workspace_missing: false,
    loading: true });
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);  // workspace obj pending archive
  const [form, setForm] = useState({ business_name: "", business_type: "",
    website: "", description: "", target_audience: "" });
  const [busy, setBusy] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const activeId = getActiveWorkspaceId();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/business-workspaces");
      setState({ ...data, loading: false });
    } catch (_e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Phase 3 §8/§9 — when workspace switches, invalidate cached data by
  // reloading the page. Simple + reliable; Phase 4 can wire query cache.
  const switchTo = (id) => {
    setActiveWorkspaceId(id || null);
    setOpen(false);
    // Full reload ensures every panel (posts, media, brand-learning,
    // autopilot, analytics, connections) fetches fresh with the new
    // header. This is the safest way to invalidate stale React state.
    if (typeof window !== "undefined") window.location.reload();
  };

  const create = async () => {
    if (!form.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/business-workspaces", form);
      toast.success(`${data.name} is ready`);
      setAddOpen(false);
      setForm({ business_name: "", business_type: "", website: "", description: "", target_audience: "" });
      // Auto-switch to the newly created workspace.
      switchTo(data.workspace_id);
    } catch (err) {
      const detail = err?.response?.data?.detail || {};
      const code = typeof detail === "object" ? detail.code : null;
      if (code === "workspace_limit_reached") {
        toast.error("Your Business plan includes up to 4 business workspaces. Additional workspaces will be available soon.");
      } else if (code === "business_plan_required") {
        toast.error("Upgrade to the Business plan to manage multiple businesses.");
      } else if (code === "owner_only") {
        toast.error("Only the Business owner can add a new business workspace.");
      } else {
        toast.error(err?.response?.data?.detail?.message || "Could not create workspace");
      }
    } finally {
      setBusy(false);
    }
  };

  // Archive a NON-PRIMARY workspace. Data is preserved on disk; the row
  // just drops out of the active selector and its background jobs stop.
  // If the archived workspace is currently active, we clear the active
  // id (falls back to primary) BEFORE reloading so the app never lands
  // on a 403 workspace_forbidden and enters the interceptor reload loop.
  const archive = async () => {
    const target = archiveTarget;
    if (!target || target.is_primary) return;
    setArchiving(true);
    try {
      await api.delete(`/business-workspaces/${target.workspace_id}`);
      toast.success(`${target.name} archived. Its data is preserved.`);
      const wasActive = getActiveWorkspaceId() === target.workspace_id;
      setArchiveTarget(null);
      if (wasActive) {
        // Reset to primary before reload; reload with no header falls
        // back to the primary workspace server-side.
        setActiveWorkspaceId(null);
      }
      if (typeof window !== "undefined") window.location.reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || {};
      const code = typeof detail === "object" ? detail.code : null;
      if (code === "cannot_archive_primary") {
        toast.error("The primary business workspace cannot be archived.");
      } else if (code === "owner_only") {
        toast.error("Only the Business owner can archive a business workspace.");
      } else if (code === "feature_not_enabled") {
        toast.error("This feature is not available.");
      } else {
        toast.error(err?.response?.data?.detail?.message || "Could not archive workspace");
      }
    } finally {
      setArchiving(false);
    }
  };

  if (state.loading) return null;

  // Feature-flag gate: server-derived. When POSTMORO_MULTI_BUSINESS_ENABLED
  // is off on the backend, the entire multi-business surface (selector,
  // Switch Business menu, Add Business) must be hidden — including for
  // Business plan users. This is the single source of truth.
  if (!state.multi_business_enabled) return null;

  // Hide entirely for non-Business users with only one workspace.
  if (!state.is_business_plan && state.count <= 1) return null;

  // Business plan with only the primary → show a minimal name + "Add Business".
  const active = state.workspaces.find((w) => w.workspace_id === activeId)
    || state.workspaces.find((w) => w.is_primary)
    || state.workspaces[0];
  const activeName = active?.name || "My Business";

  return (
    <>
      <div className="relative" data-testid="workspace-selector">
        <button
          type="button"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--bg-1)] hover:bg-[color:var(--bg-2)] text-sm font-medium text-[color:var(--text-1)] transition-colors"
          onClick={() => setOpen((v) => !v)}
          data-testid="workspace-selector-trigger"
          aria-label="Switch business"
        >
          <Building2 className="h-4 w-4 opacity-70" />
          <span className="max-w-[160px] truncate">{activeName}</span>
          <ChevronsUpDown className="h-3 w-3 opacity-60" />
        </button>
        {open && (
          <div
            className="absolute right-0 mt-2 w-64 rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--bg-1)] shadow-lg z-50 py-1 overflow-hidden"
            data-testid="workspace-selector-menu"
          >
            <div className="px-3 py-2 text-xs uppercase tracking-wide text-[color:var(--text-3)]">
              Switch Business
            </div>
            {state.workspaces.map((w) => (
              <div
                key={w.workspace_id}
                className={`flex items-center ${w.workspace_id === (active?.workspace_id) ? "bg-[color:var(--bg-2)]" : ""}`}
              >
                <button
                  type="button"
                  className="flex-1 flex items-center justify-between px-3 py-2 text-sm hover:bg-[color:var(--bg-2)] text-left"
                  onClick={() => switchTo(w.is_primary ? null : w.workspace_id)}
                  data-testid={`workspace-item-${w.workspace_id}`}
                >
                  <span className="truncate">{w.name}{w.is_primary ? " (Primary)" : ""}</span>
                  {w.workspace_id === active?.workspace_id && (
                    <Check className="h-4 w-4 opacity-70 shrink-0 ml-2" />
                  )}
                </button>
                {state.is_owner && !w.is_primary && (
                  <button
                    type="button"
                    className="px-2 py-2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--bg-2)]"
                    title={`Archive ${w.name}`}
                    aria-label={`Archive ${w.name}`}
                    onClick={(e) => { e.stopPropagation(); setOpen(false); setArchiveTarget(w); }}
                    data-testid={`workspace-archive-${w.workspace_id}`}
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {state.is_owner && (
              <>
                <div className="border-t border-[color:var(--border-subtle)] my-1" />
                {state.primary_workspace_missing ? (
                  <div
                    className="px-3 py-2 text-xs text-[color:var(--danger)]"
                    data-testid="workspace-primary-missing-warning"
                  >
                    Your primary business workspace is missing. Contact
                    support to restore it before adding another business.
                  </div>
                ) : state.can_add ? (
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[color:var(--brand-primary)] hover:bg-[color:var(--bg-2)]"
                    onClick={() => { setOpen(false); setAddOpen(true); }}
                    data-testid="workspace-add-business"
                  >
                    <Plus className="h-4 w-4" /> Add Business
                  </button>
                ) : state.is_business_plan ? (
                  <div className="px-3 py-2 text-xs text-[color:var(--text-3)]" data-testid="workspace-limit-message">
                    {state.count} of {state.limit} workspaces used.
                    <br />
                    Additional workspaces will be available soon.
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="workspace-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Add a new business
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="ws-name">Business name</Label>
              <Input
                id="ws-name"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Bella Spa"
                data-testid="workspace-name-input"
              />
            </div>
            <div>
              <Label htmlFor="ws-type">Business type (optional)</Label>
              <Input
                id="ws-type"
                value={form.business_type}
                onChange={(e) => setForm({ ...form, business_type: e.target.value })}
                placeholder="Wellness & spa"
                data-testid="workspace-type-input"
              />
            </div>
            <div>
              <Label htmlFor="ws-site">Website (optional)</Label>
              <Input
                id="ws-site"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://bellaspa.com"
              />
            </div>
            <div>
              <Label htmlFor="ws-desc">Short description (optional)</Label>
              <Textarea
                id="ws-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="What does this business do?"
              />
            </div>
            <div>
              <Label htmlFor="ws-aud">Target audience (optional)</Label>
              <Textarea
                id="ws-aud"
                value={form.target_audience}
                onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                rows={2}
                placeholder="Who are your customers?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="workspace-add-submit">
              {busy ? "Adding..." : "Add Business"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}>
        <DialogContent data-testid="workspace-archive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" /> Archive {archiveTarget?.name || "business"}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[color:var(--text-2)]">
            <p>
              This business will be removed from your active workspace list.
              Its posts, media, analytics, connections, and history will be
              preserved.
            </p>
            <p className="text-xs text-[color:var(--text-3)]">
              You can create a new business in its place. Autopilot and any
              scheduled posts for this workspace will be paused.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveTarget(null)} data-testid="workspace-archive-cancel">
              Cancel
            </Button>
            <Button onClick={archive} disabled={archiving} data-testid="workspace-archive-confirm">
              {archiving ? "Archiving..." : "Archive Business"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
